import { Directive, DirectiveBinding, nextTick } from 'vue';

/**
 * @typedef {'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'} MovablePosition
 * Defines the possible initial positions for the movable element.
 * 'start' and 'end' are resolved based on LTR/RTL text direction.
 */
type MovablePosition = 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

/**
 * Pixel insets from the boundary: one vertical (`top` or `bottom`) and one horizontal
 * (`left`, `right`, `start`, or `end`). `start` / `end` follow the element’s `direction` (LTR/RTL).
 */
export type MovablePositionInset =
  | ({ top: number } & ({ left: number } | { right: number } | { start: number } | { end: number }))
  | ({ bottom: number } & ({ left: number } | { right: number } | { start: number } | { end: number }));

/**
 * @typedef {object} MovableOptions
 * @property {string} [area] - CSS selector for the boundary element. Defaults to `'body'`.
 * @property {string} [trigger] - Selector within the movable element for the drag handle.
 * @property {MovablePosition | MovablePositionInset} [position] - Preset corner or pixel insets from the boundary.
 * @property {boolean} [fixed] - Use `position: fixed` instead of `absolute`.
 */
export interface MovableOptions {
  area?: string;
  trigger?: string;
  position?: MovablePosition | MovablePositionInset;
  fixed?: boolean;
}

/**
 * An extended HTML element type that includes a custom cleanup function.
 */
interface MovableElement extends HTMLElement {
  _cleanup?: () => void;
}

/**
 * A Vue directive that makes an element movable within a specified boundary.
 *
 * @example
 * // Basic usage (boundary is viewport)
 * <div v-movable></div>
 *
 * @example
 * // With options
 * <div v-movable="{ area: '#container', position: 'bottom-end' }"></div>
 *
 * @example
 * // Pixel insets from boundary edges (RTL-aware for start/end)
 * <div v-movable="{ area: '#container', position: { bottom: 16, end: 12 } }"></div>
 */
export const movable: Directive<MovableElement, MovableOptions | undefined> = {
  mounted(element: MovableElement, binding: DirectiveBinding<MovableOptions | undefined>) {
    const options: MovableOptions = binding.value || {};
    const boundarySelector = options.area || 'body';
    const movingTrigger: HTMLElement | null = options.trigger ? element.querySelector(options.trigger) : null;
    const trigger: HTMLElement = movingTrigger || element;
    let boundaryElement: HTMLElement | null = document.querySelector(boundarySelector);

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let offsetX = 0;
    let offsetY = 0;

    // --- Initial Styles ---
    let hasMoved = false;
    let initialDragX = 0;
    let initialDragY = 0;
    const DRAG_THRESHOLD = 5;

    element.style.position = options.fixed ? 'fixed' : 'absolute';
    trigger.style.cursor = 'move';
    element.style.userSelect = 'none';
    trigger.style.touchAction = 'none';

    /**
     * Sets the element's position using CSS transform.
     * @param {number} x - The horizontal offset in pixels.
     * @param {number} y - The vertical offset in pixels.
     */
    const setPosition = (x: number, y: number) => {
      element.style.transform = `translate(${x}px, ${y}px)`;
    };

    /**
     * Calculates and sets the desired initial position based on the 'position' option.
     * This function only calculates the target offsets; it does not validate them.
     */
    const applyInitialPosition = () => {
      const position = options.position;
      if (!position || !boundaryElement) {
        return;
      }

      const direction = window.getComputedStyle(element).direction;
      const elementRect = element.getBoundingClientRect();
      const boundaryRect = boundaryElement.getBoundingClientRect();

      const targetYTop = boundaryRect.top - elementRect.top;
      const targetYBottom = boundaryRect.bottom - elementRect.bottom;
      const targetXLeft = boundaryRect.left - elementRect.left;
      const targetXRight = boundaryRect.right - elementRect.right;

      const targetXStart = direction === 'rtl' ? targetXRight : targetXLeft;
      const targetXEnd = direction === 'rtl' ? targetXLeft : targetXRight;
      const isRtl = direction === 'rtl';

      if (typeof position === 'string') {
        switch (position) {
          case 'top-start':
            offsetY = targetYTop;
            offsetX = targetXStart;
            break;
          case 'top-end':
            offsetY = targetYTop;
            offsetX = targetXEnd;
            break;
          case 'bottom-start':
            offsetY = targetYBottom;
            offsetX = targetXStart;
            break;
          case 'bottom-end':
            offsetY = targetYBottom;
            offsetX = targetXEnd;
            break;
        }
      } else {
        if ('top' in position) {
          offsetY = targetYTop + position.top;
        } else {
          offsetY = targetYBottom - position.bottom;
        }

        if ('left' in position) {
          offsetX = targetXLeft + position.left;
        } else if ('right' in position) {
          offsetX = targetXRight - position.right;
        } else if ('start' in position) {
          offsetX = isRtl ? targetXRight - position.start : targetXLeft + position.start;
        } else {
          offsetX = isRtl ? targetXLeft + position.end : targetXRight - position.end;
        }
      }

      setPosition(offsetX, offsetY);
    };

    /**
     * Clamps the element's current offsets to ensure it stays within the boundary.
     * After clamping, it applies the final position.
     */
    const constrainToBounds = () => {
      if (!boundaryElement) {
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const boundaryRect = boundaryElement.getBoundingClientRect();

      const minX = boundaryRect.left - (elementRect.left - offsetX);
      const maxX = boundaryRect.right - (elementRect.right - offsetX);
      const minY = boundaryRect.top - (elementRect.top - offsetY);
      const maxY = boundaryRect.bottom - (elementRect.bottom - offsetY);

      offsetX = Math.max(minX, Math.min(offsetX, maxX));
      offsetY = Math.max(minY, Math.min(offsetY, maxY));

      setPosition(offsetX, offsetY);
    };

    /**
     * Re-queries for the boundary element and re-applies constraints.
     * Useful on window resize.
     */
    const updateBoundary = () => {
      boundaryElement = document.querySelector(boundarySelector);
      constrainToBounds();
    };

    // --- Drag Event Handlers ---

    /**
     * Prevents the `click` event from firing if a drag operation has occurred.
     *
     * This function is registered as a temporary event listener during the capture phase
     * of a `mousedown` or `touchstart` event. It checks the `hasMoved` flag, which is set
     * to true only if the cursor or touch-point moves beyond a defined threshold.
     * If a drag is detected (`hasMoved` is true), it calls `preventDefault()` and
     * `stopPropagation()` on the `click` event to avoid unintended actions,
     * such as activating a button within the draggable element.
     *
     * @param {MouseEvent} event The `click` event object to be potentially suppressed.
     */
    const preventClickAfterDrag = (event: MouseEvent) => {
      if (hasMoved) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    /*
     * Initiates the drag operation on mousedown.
     * @param {MouseEvent | TouchEvent} event - The mouse event.
     */
    const startDrag = (event: MouseEvent | TouchEvent) => {
      const point = 'touches' in event ? event.touches[0] : event;

      if (!point) {
        return;
      }

      isDragging = true;
      hasMoved = false;

      initialDragX = point.clientX;
      initialDragY = point.clientY;

      startX = point.clientX - offsetX;
      startY = point.clientY - offsetY;

      trigger.style.cursor = 'grabbing';

      document.addEventListener('mousemove', drag);
      document.addEventListener('mouseup', stopDrag);

      document.addEventListener('touchmove', drag, { passive: false });
      document.addEventListener('touchend', stopDrag);
      document.addEventListener('touchcancel', stopDrag);

      trigger.addEventListener('click', preventClickAfterDrag, true);
    };

    /**
     * Handles the element's movement during drag on mousemove.
     * @param {MouseEvent | TouchEvent} event - The mouse event.
     */
    const drag = (event: MouseEvent | TouchEvent) => {
      const point = 'touches' in event ? event.touches[0] : event;

      if (!isDragging || !boundaryElement || !point) {
        return;
      }

      if ('touches' in event && event.cancelable) {
        event.preventDefault();
      }

      if (!hasMoved) {
        const dx = Math.abs(point.clientX - initialDragX);
        const dy = Math.abs(point.clientY - initialDragY);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
          hasMoved = true;
        }
      }

      const newX = point.clientX - startX;
      const newY = point.clientY - startY;

      // The clamping logic is duplicated here to avoid recalculating rects
      // within constrainToBounds, providing smoother dragging.
      const elementRect = element.getBoundingClientRect();
      const boundaryRect = boundaryElement.getBoundingClientRect();

      const minX = boundaryRect.left - (elementRect.left - offsetX);
      const maxX = boundaryRect.right - (elementRect.right - offsetX);
      const minY = boundaryRect.top - (elementRect.top - offsetY);
      const maxY = boundaryRect.bottom - (elementRect.bottom - offsetY);

      offsetX = Math.max(minX, Math.min(newX, maxX));
      offsetY = Math.max(minY, Math.min(newY, maxY));

      setPosition(offsetX, offsetY);
    };

    /**
     * Terminates the drag operation on mouseup.
     */
    const stopDrag = () => {
      isDragging = false;
      trigger.style.cursor = 'move';
      document.removeEventListener('mousemove', drag);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchmove', drag);
      document.removeEventListener('touchend', stopDrag);
      document.removeEventListener('touchcancel', stopDrag);

      setTimeout(() => {
        trigger.removeEventListener('click', preventClickAfterDrag, true);
      }, 0);
    };

    // --- Lifecycle and Cleanup ---

    window.addEventListener('resize', updateBoundary);
    trigger.addEventListener('mousedown', startDrag);
    trigger.addEventListener('touchstart', startDrag, { passive: false });

    element._cleanup = () => {
      window.removeEventListener('resize', updateBoundary);
      trigger.removeEventListener('mousedown', startDrag);
      trigger.removeEventListener('touchstart', startDrag);
      trigger.removeEventListener('click', preventClickAfterDrag, true);

      stopDrag();
    };

    nextTick(() => {
      boundaryElement = document.querySelector(boundarySelector);
      // First, calculate the ideal starting position
      applyInitialPosition();
      // Then, clamp it to the boundary and apply the transform
      constrainToBounds();
    });
  },

  unmounted(element: MovableElement) {
    if (element._cleanup) {
      element._cleanup();
    }
  },
};

export default movable;
